/**
 * Client Data Model
 * Represents individual debtors with comprehensive contact and identification information
 */

// Client field validation rules
export const CLIENT_VALIDATION_RULES = {
  clientName: {
    required: true,
    minLength: 1,
    maxLength: 100
  },
  debtCode: {
    required: true,
    minLength: 1,
    maxLength: 20
  },
  homeAddress: {
    required: false,
    maxLength: 200
  },
  phone: {
    required: false,
    pattern: /^[\d\s\-\(\)\+\.]*$/
  },
  workPhone: {
    required: false,
    pattern: /^[\d\s\-\(\)\+\.]*$/
  },
  cell: {
    required: false,
    pattern: /^[\d\s\-\(\)\+\.]*$/
  },
  fax: {
    required: false,
    pattern: /^[\d\s\-\(\)\+\.]*$/
  },
  email: {
    required: false,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  },
  contact: {
    required: false,
    maxLength: 100
  },
  drLic: {
    required: false,
    maxLength: 20
  },
  ssn: {
    required: false,
    pattern: /^\d{3}-?\d{2}-?\d{4}$/
  },
  acctRep: {
    required: false,
    maxLength: 50
  },
  alias: {
    required: false,
    maxLength: 100
  },
  comment: {
    required: false,
    maxLength: 500
  }
};

// Available debt codes
export const DEBT_CODES = [
  'CC01', 'CC02', 'CC03', 'CC04', 'CC05',
  'PL01', 'PL02', 'PL03', 'PL04', 'PL05',
  'AU01', 'AU02', 'AU03', 'AU04', 'AU05',
  'MG01', 'MG02', 'MG03', 'MG04', 'MG05',
  'ST01', 'ST02', 'ST03', 'ST04', 'ST05'
];

/**
 * Creates a new Client object with default values
 * @param {Object} data - Initial client data
 * @returns {Object} Client object
 */
export const createClient = (data = {}) => {
  const now = new Date();
  
  // Helper function to safely convert to string
  const safeString = (value) => {
    if (value === null || value === undefined) return '';
    return String(value);
  };
  
  return {
    id: data.id || generateClientId(),
    clientName: safeString(data.clientName),
    debtCode: safeString(data.debtCode),
    homeAddress: safeString(data.homeAddress),
    phone: safeString(data.phone),
    workPhone: safeString(data.workPhone),
    cell: safeString(data.cell),
    fax: safeString(data.fax),
    email: safeString(data.email),
    contact: safeString(data.contact),
    drLic: safeString(data.drLic),
    ssn: safeString(data.ssn),
    acctRep: safeString(data.acctRep),
    alias: safeString(data.alias),
    comment: safeString(data.comment),
    createdAt: data.createdAt || now,
    updatedAt: data.updatedAt || now
  };
};

/**
 * Validates a client object against the validation rules
 * @param {Object} client - Client object to validate
 * @returns {Object} Validation result with isValid boolean and errors array
 */
export const validateClient = (client) => {
  const errors = [];
  
  // Helper function to safely convert to string and trim
  const safeStringTrim = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  };
  
  // Helper function to safely get string length
  const safeStringLength = (value) => {
    if (value === null || value === undefined) return 0;
    return String(value).length;
  };
  
  // Check required fields
  const clientNameStr = safeStringTrim(client.clientName);
  if (!clientNameStr || clientNameStr.length === 0) {
    errors.push('Client name is required');
  }
  
  const debtCodeStr = safeStringTrim(client.debtCode);
  if (!debtCodeStr || debtCodeStr.length === 0) {
    errors.push('Debt code is required');
  }
  
  // Validate field lengths
  if (client.clientName && safeStringLength(client.clientName) > CLIENT_VALIDATION_RULES.clientName.maxLength) {
    errors.push(`Client name must be ${CLIENT_VALIDATION_RULES.clientName.maxLength} characters or less`);
  }
  
  if (client.debtCode && safeStringLength(client.debtCode) > CLIENT_VALIDATION_RULES.debtCode.maxLength) {
    errors.push(`Debt code must be ${CLIENT_VALIDATION_RULES.debtCode.maxLength} characters or less`);
  }
  
  if (client.homeAddress && safeStringLength(client.homeAddress) > CLIENT_VALIDATION_RULES.homeAddress.maxLength) {
    errors.push(`Home address must be ${CLIENT_VALIDATION_RULES.homeAddress.maxLength} characters or less`);
  }
  
  // Validate phone number patterns
  const phoneFields = ['phone', 'workPhone', 'cell', 'fax'];
  phoneFields.forEach(field => {
    const phoneValue = client[field];
    if (phoneValue && String(phoneValue).trim() && !CLIENT_VALIDATION_RULES.phone.pattern.test(String(phoneValue))) {
      errors.push(`${field} contains invalid characters`);
    }
  });
  
  // Validate email format
  if (client.email && String(client.email).trim() && !CLIENT_VALIDATION_RULES.email.pattern.test(String(client.email))) {
    errors.push('Email format is invalid');
  }
  
  // Validate SSN format
  if (client.ssn && String(client.ssn).trim() && !CLIENT_VALIDATION_RULES.ssn.pattern.test(String(client.ssn))) {
    errors.push('SSN format is invalid (expected: XXX-XX-XXXX)');
  }
  
  // Validate debt code is in allowed list
  if (client.debtCode && String(client.debtCode).trim() && !DEBT_CODES.includes(String(client.debtCode))) {
    errors.push('Invalid debt code selected');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Generates a unique client ID
 * @returns {string} Unique client ID
 */
const generateClientId = () => {
  return 'client_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

/**
 * Searches clients by name or debt code
 * @param {Array} clients - Array of client objects
 * @param {string} searchTerm - Search term
 * @returns {Array} Filtered array of clients
 */
export const searchClients = (clients, searchTerm) => {
  if (!searchTerm || String(searchTerm).trim().length === 0) {
    return clients;
  }
  
  const term = String(searchTerm).toLowerCase().trim();
  return clients.filter(client => {
    const clientName = String(client.clientName || '').toLowerCase();
    const debtCode = String(client.debtCode || '').toLowerCase();
    return clientName.includes(term) || debtCode.includes(term);
  });
};